import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/solid';
import ConfirmationModal from './utils/ConfirmationModal';
import useBlockDevice from '../hooks/useBlockDevice';
import useReleaseDevice from '../hooks/useReleaseDevice';
import usePermission from '../authorization/permissionHook';
import useAnalytics from '../hooks/useAnalytics';

/**
 * Permission constants for Device Defender actions
 * These must match the permissions defined in roles.json
 */
const BLOCK_DEVICE_PERMISSION = 'blockDevice';
const RELEASE_DEVICE_PERMISSION = 'releaseDevice';

/**
 * DeviceDefenderActions component
 * Renders Block/Unblock action buttons for Device Defender quarantine management
 * 
 * @param {Object} props - Component props
 * @param {number} props.projectId - Project ID for multi-tenant isolation
 * @param {string} props.deviceSerial - Device serial number
 * @param {boolean} props.isBlocked - Whether the device is currently blocked
 * @param {string} props.defenderStatus - Device Defender status ('active', 'blocked', 'not-tracked', 'loading', 'unknown')
 * @param {Function} [props.onActionComplete] - Optional callback after action completes
 */
const DeviceDefenderActions = ({ projectId, deviceSerial, isBlocked, defenderStatus, onActionComplete }) => {
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);

  // Analytics tracking
  const { setAnalytics } = useAnalytics();

  // Permission checks
  const [, canBlockDevice] = usePermission([BLOCK_DEVICE_PERMISSION]);
  const [, canReleaseDevice] = usePermission([RELEASE_DEVICE_PERMISSION]);

  // Mutation hooks
  const { blockDevice, isLoading: isBlocking } = useBlockDevice({
    onSuccess: () => {
      // Track success analytics
      setAnalytics({
        event: 'custom_event',
        extraParams: {
          type: 'action_completed',
          name: 'device_management-overview-device_defender-block_device-success',
          device_serial: deviceSerial,
        },
      });
      setIsBlockModalOpen(false);
      if (onActionComplete) onActionComplete();
    },
    onError: (error) => {
      // Track error analytics
      setAnalytics({
        event: 'custom_event',
        extraParams: {
          type: 'action_failed',
          name: 'device_management-overview-device_defender-block_device-error',
          device_serial: deviceSerial,
          error_message: error?.message || 'Unknown error',
        },
      });
    },
  });

  const { releaseDevice, isLoading: isReleasing } = useReleaseDevice({
    onSuccess: () => {
      // Track success analytics
      setAnalytics({
        event: 'custom_event',
        extraParams: {
          type: 'action_completed',
          name: 'device_management-overview-device_defender-unblock_device-success',
          device_serial: deviceSerial,
        },
      });
      setIsReleaseModalOpen(false);
      if (onActionComplete) onActionComplete();
    },
    onError: (error) => {
      // Track error analytics
      setAnalytics({
        event: 'custom_event',
        extraParams: {
          type: 'action_failed',
          name: 'device_management-overview-device_defender-unblock_device-error',
          device_serial: deviceSerial,
          error_message: error?.message || 'Unknown error',
        },
      });
    },
  });

  // Analytics handlers
  const handleBlockButtonClick = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-device_defender-block_device',
        device_serial: deviceSerial,
      },
    });
    setIsBlockModalOpen(true);
  };

  const handleReleaseButtonClick = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-device_defender-unblock_device',
        device_serial: deviceSerial,
      },
    });
    setIsReleaseModalOpen(true);
  };

  const handleBlockConfirm = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-device_defender-block_device-confirm',
        device_serial: deviceSerial,
      },
    });
    blockDevice({ projectId, deviceSerial });
  };

  const handleBlockCancel = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-device_defender-block_device-cancel',
        device_serial: deviceSerial,
      },
    });
    setIsBlockModalOpen(false);
  };

  const handleReleaseConfirm = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-device_defender-unblock_device-confirm',
        device_serial: deviceSerial,
      },
    });
    releaseDevice({ projectId, deviceSerial });
  };

  const handleReleaseCancel = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-device_defender-unblock_device-cancel',
        device_serial: deviceSerial,
      },
    });
    setIsReleaseModalOpen(false);
  };

  // If user doesn't have any permission, or device is not tracked, don't render anything
  if (!canBlockDevice && !canReleaseDevice) {
    return null;
  }

  // Don't show buttons while loading or for devices that are not tracked by Device Defender
  if (defenderStatus === 'loading' || defenderStatus === 'not-tracked') {
    return null;
  }

  return (
    <>
      {/* Block Button - Only show if not blocked and user has permission */}
      {!isBlocked && canBlockDevice && (
        <button
          type="button"
          onClick={handleBlockButtonClick}
          disabled={isBlocking}
          className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-red-600 border border-transparent rounded shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed ml-3"
        >
          <ShieldExclamationIcon className="w-3 h-3 mr-1" />
          {isBlocking ? 'Blocking...' : 'Block'}
        </button>
      )}

      {/* Unblock Button - Only show if blocked and user has permission */}
      {isBlocked && canReleaseDevice && (
        <button
          type="button"
          onClick={handleReleaseButtonClick}
          disabled={isReleasing}
          className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-green-600 border border-transparent rounded shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed ml-3"
        >
          <ShieldCheckIcon className="w-3 h-3 mr-1" />
          {isReleasing ? 'Releasing...' : 'Unblock'}
        </button>
      )}

      {/* Block Confirmation Modal */}
      <ConfirmationModal
        isOpen={isBlockModalOpen}
        onClose={handleBlockCancel}
        onConfirm={handleBlockConfirm}
        title="Block Device"
        message={`Are you sure you want to block device "${deviceSerial}"? This will prevent the device from sending data to the IoT platform.`}
        confirmText="Yes, Block Device"
        cancelText="Cancel"
      />

      {/* Release Confirmation Modal */}
      <ConfirmationModal
        isOpen={isReleaseModalOpen}
        onClose={handleReleaseCancel}
        onConfirm={handleReleaseConfirm}
        title="Unblock Device"
        message={`Are you sure you want to unblock device "${deviceSerial}"? This will allow the device to resume normal operation.`}
        confirmText="Yes, Unblock Device"
        cancelText="Cancel"
      />
    </>
  );
};

DeviceDefenderActions.propTypes = {
  projectId: PropTypes.number.isRequired,
  deviceSerial: PropTypes.string.isRequired,
  isBlocked: PropTypes.bool.isRequired,
  defenderStatus: PropTypes.oneOf(['active', 'blocked', 'not-tracked', 'loading', 'unknown']),
  onActionComplete: PropTypes.func,
};

DeviceDefenderActions.defaultProps = {
  defenderStatus: 'unknown',
  onActionComplete: null,
};

export default DeviceDefenderActions;
